
import Link from '@/components/compat/Link';
import { usePathname } from '@/lib/navigation';
import { Menu, X, Home, BookOpen } from 'lucide-react';
import { useState, useEffect } from 'react';
import { smileyStripUrl, cn } from '@/components/help-center/latest-power-shared';
import { AceIcon } from '@/components/help-center/aces/AceIcon';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import {
  EquipeDesktopDropdown,
  EquipeMobileSection,
} from '@/components/help-center/nav/EquipeNavSubmenu';
import { useCms } from '@/components/painel/cms/cms-context';
import { normalizeIdentity } from '@/lib/site-defaults';

const changelogNavButtonClass = cn(
  'group relative flex items-center gap-2 overflow-hidden rounded-full py-2 pl-2 pr-4',
  'font-poppins text-sm font-semibold text-white',
  'bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700',
  'ring-1 ring-sky-200/20',
  'transition-all duration-200',
  'hover:from-sky-300 hover:via-blue-400 hover:to-blue-600',
  'hover:ring-sky-200/35'
);

const changelogNavButtonActiveClass =
  'from-sky-300 via-blue-400 to-blue-600 ring-2 ring-sky-200/40';

const changelogButtonShineClass = cn(
  'pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/20 via-transparent to-white/15',
  'opacity-60 transition-opacity duration-200 group-hover:opacity-80 group-hover:to-white/25'
);

const changelogNewBadgeClass = cn(
  'relative rounded-full bg-[#030023]/30 px-2 py-0.5',
  'text-[9px] font-bold uppercase tracking-[0.12em] text-white backdrop-blur-[2px]',
  'transition-colors duration-200 group-hover:bg-white/20'
);

export default function Navbar() {
  const pathname = usePathname();
  const { site } = useCms();
  const siteTitle = normalizeIdentity(site.identity).title;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileEquipeOpen, setMobileEquipeOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileEquipeOpen(false);
  }, [pathname]);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-[60]"
      style={{ paddingRight: 'var(--app-scroll-lock-gap, 0px)' }}
    >
      <div
        className={`absolute inset-0 bg-gradient-to-r from-[#030023] to-[#000b6a] shadow-lg shadow-black/25 transition-opacity duration-300 ${scrolled ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="relative flex items-center justify-between h-20">
          <Link href="/" className="flex shrink-0 items-center gap-1.5">
            <span className="font-poppins text-4xl font-bold tracking-tight text-white">
              {siteTitle}
            </span>
          </Link>

          <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex items-center gap-1">
            <Link
              href="/"
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 hover:bg-white/20 font-poppins',
                pathname === '/' && 'bg-white/15'
              )}
            >
              <Home className="w-4 h-4" />
              Início
            </Link>
            <Link
              href="/regras"
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 hover:bg-white/20 font-poppins',
                pathname === '/regras' && 'bg-white/15'
              )}
            >
              <BookOpen className="w-4 h-4" />
              Regras
            </Link>

            <EquipeDesktopDropdown pathname={pathname} />

            <Link
              href="/powers"
              className={cn(
                'nav-special-powers flex items-center rounded-lg px-4 py-2 font-poppins text-sm font-medium text-sky-200 transition-colors duration-200 hover:bg-sky-500/20 hover:text-white',
                pathname.startsWith('/powers') && 'bg-sky-500/20 text-white'
              )}
            >
              <span className="nav-special-content">
                <MaskedSvgIcon src="/icons/power.svg" className="nav-special-icon h-4 w-4" />
                <span className="nav-special-label">Powers</span>
              </span>
            </Link>
            <Link
              href="/aces"
              className={cn(
                'nav-special-aces flex items-center rounded-lg px-4 py-2 font-poppins text-sm font-medium text-amber-200 transition-colors duration-200 hover:bg-amber-500/20 hover:text-white',
                pathname.startsWith('/aces') && 'bg-amber-500/20 text-white'
              )}
            >
              <span className="nav-special-content">
                <AceIcon className="nav-special-icon h-4 w-4 text-amber-300" />
                <span className="nav-special-label">Aces</span>
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center shrink-0">
            <Link
              href="/changelog"
              className={cn(
                changelogNavButtonClass,
                pathname.startsWith('/changelog') && changelogNavButtonActiveClass
              )}
            >
              <span className={changelogButtonShineClass} aria-hidden />
              <span className="relative z-10 flex items-center gap-0.5">
                <img
                  src={smileyStripUrl('journalist')}
                  alt=""
                  className="h-7 w-7 shrink-0 object-contain"
                  aria-hidden
                />
                <span>Changelog</span>
              </span>
              <span className={cn(changelogNewBadgeClass, 'z-10')}>NEW</span>
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white hover:text-blue-100"
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="relative md:hidden border-t border-white/20">
          <div
            className={`absolute inset-0 transition-opacity duration-300 ${scrolled ? 'opacity-100 bg-gradient-to-r from-[#030023] to-[#000b6a]' : 'opacity-100 bg-[#030023]/95 backdrop-blur-md'}`}
            aria-hidden
          />
          <div className="relative px-6 py-4 space-y-1">
            <Link
              href="/"
              className="block text-sm font-medium text-white hover:text-blue-100 transition-colors py-2 font-poppins"
              onClick={() => setMobileMenuOpen(false)}
            >
              Início
            </Link>
            <Link
              href="/regras"
              className="block text-sm font-medium text-white hover:text-blue-100 transition-colors py-2 font-poppins"
              onClick={() => setMobileMenuOpen(false)}
            >
              Regras
            </Link>

            <EquipeMobileSection
              pathname={pathname}
              open={mobileEquipeOpen}
              onToggle={() => setMobileEquipeOpen((o) => !o)}
              onNavigate={() => setMobileMenuOpen(false)}
            />

            <Link
              href="/powers"
              className="nav-special-powers flex w-fit items-center rounded-lg px-2 py-2 font-poppins text-sm font-medium text-sky-200 transition-colors hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="nav-special-content">
                <MaskedSvgIcon src="/icons/power.svg" className="nav-special-icon h-4 w-4" />
                <span className="nav-special-label">Powers</span>
              </span>
            </Link>
            <Link
              href="/aces"
              className="nav-special-aces flex w-fit items-center rounded-lg px-2 py-2 font-poppins text-sm font-medium text-amber-200 transition-colors hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="nav-special-content">
                <AceIcon className="nav-special-icon h-4 w-4 text-amber-300" />
                <span className="nav-special-label">Aces</span>
              </span>
            </Link>
            <Link
              href="/changelog"
              className={cn(
                changelogNavButtonClass,
                'mt-2 w-fit',
                pathname.startsWith('/changelog') && changelogNavButtonActiveClass
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className={changelogButtonShineClass} aria-hidden />
              <span className="relative z-10 flex items-center gap-0.5">
                <img
                  src={smileyStripUrl('journalist')}
                  alt=""
                  className="h-5 w-5 shrink-0 object-contain"
                  aria-hidden
                />
                <span>Changelog</span>
              </span>
              <span className={cn(changelogNewBadgeClass, 'z-10')}>NEW</span>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
